"use client";

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import Image from "next/image";
import { useUser } from "@clerk/nextjs";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ImagePlus,
  Loader2,
  Lock,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ProblemAnswer from "@/components/ProblemAnswer";
import type { RestorationBlock, RestorationExamSession, RestorationPost, RestorationPostBody, RestorationPostStatus } from "@/lib/server-actions/restoration/_shared";
import { createPost, updateOwnPost, updatePostAsAdmin } from "@/lib/server-actions/restoration/posts";
import {
  blocksToProblemString,
  validateBodyClient,
} from "@/lib/restorationBlocks";

interface PostEditorProps {
  sessions: RestorationExamSession[];
  isAdmin: boolean;
  initialSessionId?: number;
  /** 수정 모드 활성화 — 존재하면 createPost 대신 updateOwnPost/updatePostAsAdmin 호출 */
  editingPost?: RestorationPost;
  /** 수정 모드일 때 본인이 아닌 관리자가 수정 중인지 */
  adminEditing?: boolean;
  onSuccess?: (post: RestorationPost | null) => void;
  onCancel?: () => void;
}

type AnswerMode = "short" | "choice";

type ImageUploadState = "idle" | "uploading" | "error";

const BLOCK_LANGUAGES = [
  "c",
  "java",
  "python",
  "sql",
  "txt",
];

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** naturalWidth/Height를 비동기로 읽어오는 헬퍼. */
function readImageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth || 1200, height: img.naturalHeight || 800 });
    img.onerror = () => resolve({ width: 1200, height: 800 });
    img.src = url;
  });
}

export default function PostEditor({
  sessions,
  isAdmin,
  initialSessionId,
  editingPost,
  adminEditing = false,
  onSuccess,
  onCancel,
}: PostEditorProps) {
  const { isSignedIn } = useUser();
  const isEditMode = !!editingPost;

  const writableSessions = useMemo(
    () => (isAdmin ? sessions : sessions.filter((s) => !s.is_write_locked)),
    [sessions, isAdmin]
  );

  const defaultSessionId = useMemo(() => {
    if (editingPost) return editingPost.session_id;
    if (initialSessionId && sessions.some((s) => s.id === initialSessionId)) {
      return initialSessionId;
    }
    return writableSessions[0]?.id ?? sessions[0]?.id ?? null;
  }, [editingPost, initialSessionId, sessions, writableSessions]);

  const initialAnswerMode: AnswerMode = editingPost?.body.choices?.length
    ? "choice"
    : "short";
  const initialShort =
    editingPost && typeof editingPost.body.correctAnswer === "string"
      ? editingPost.body.correctAnswer
      : "";
  const initialSelected =
    editingPost && Array.isArray(editingPost.body.correctAnswer)
      ? editingPost.body.correctAnswer
      : editingPost && typeof editingPost.body.correctAnswer === "string"
        ? editingPost.body.choices?.includes(editingPost.body.correctAnswer)
          ? [editingPost.body.correctAnswer]
          : []
        : [];

  // 사용자가 select로 명시적으로 고른 회차. null이면 defaultSessionId를 따른다.
  // useEffect로 setSessionId(defaultSessionId)를 호출하던 패턴을 derived state로 대체.
  // (sessions prop이 늦게 로드되면 defaultSessionId가 변하고, 사용자가 아직 선택하지 않았다면 자동으로 새 default를 반영)
  const [userSessionId, setUserSessionId] = useState<number | null>(null);
  const sessionId = userSessionId ?? defaultSessionId;
  const [problemNumber, setProblemNumber] = useState<number | null>(null);
  const [adminStatus, setAdminStatus] = useState<RestorationPostStatus>(
    "confirmed"
  );
  const [preamble, setPreamble] = useState(editingPost?.preamble_text ?? "");
  const [closing, setClosing] = useState(editingPost?.closing_text ?? "");
  const [blocks, setBlocks] = useState<RestorationBlock[]>(
    editingPost?.body.blocks ?? [{ type: "text", content: "" }]
  );
  const [choices, setChoices] = useState<string[]>(
    editingPost?.body.choices ?? []
  );
  const [answerMode, setAnswerMode] = useState<AnswerMode>(initialAnswerMode);
  const [shortAnswer, setShortAnswer] = useState(initialShort);
  const [selectedChoices, setSelectedChoices] =
    useState<string[]>(initialSelected);

  const [isPending, startTransition] = useTransition();
  const [imageStates, setImageStates] = useState<Record<number, ImageUploadState>>(
    {}
  );
  const [previewId] = useState(() => `restoration-preview-${uid()}`);

  const anyUploading = useMemo(
    () => Object.values(imageStates).some((s) => s === "uploading"),
    [imageStates]
  );

  // === 블록 편집 ===

  const updateBlock = (index: number, next: RestorationBlock) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? next : b)));
  };

  const addBlock = (type: RestorationBlock["type"]) => {
    setBlocks((prev) => {
      const fresh: RestorationBlock =
        type === "text"
          ? { type: "text", content: "" }
          : type === "code"
            ? { type: "code", content: "", language: "txt" }
            : type === "table"
              ? { type: "table", headers: ["열1", "열2"], rows: [["", ""]] }
              : { type: "image", url: "", alt: "" };
      return [...prev, fresh];
    });
  };

  const removeBlock = (index: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index));
    setImageStates((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const moveBlock = (index: number, dir: -1 | 1) => {
    setBlocks((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // === 이미지 업로드 ===

  const handleImageFile = useCallback(
    async (index: number, file: File) => {
      if (!ALLOWED_MIME.includes(file.type)) {
        toast.error("JPEG/PNG/GIF/WebP 이미지만 업로드할 수 있습니다.");
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error("이미지 크기는 5MB 이하여야 합니다.");
        return;
      }
      setImageStates((prev) => ({ ...prev, [index]: "uploading" }));
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch("/api/upload-restoration-image", {
          method: "POST",
          body: formData,
        });
        const data = await res.json();
        if (!data.success || !data.url) {
          throw new Error(data.error || "업로드 실패");
        }
        const { width, height } = await readImageSize(data.url);
        setBlocks((prev) =>
          prev.map((b, i) =>
            i === index
              ? { type: "image", url: data.url, alt: "", width, height }
              : b
          )
        );
        setImageStates((prev) => ({ ...prev, [index]: "idle" }));
      } catch (err) {
        console.error("upload error:", err);
        toast.error("이미지 업로드에 실패했습니다.");
        setImageStates((prev) => ({ ...prev, [index]: "error" }));
      }
    },
    []
  );

  // === 보기 / 정답 ===

  const addChoice = () => {
    setChoices((prev) => [...prev, ""]);
    if (answerMode === "short") setAnswerMode("choice");
  };

  const updateChoice = (index: number, value: string) => {
    setChoices((prev) => prev.map((c, i) => (i === index ? value : c)));
  };

  const removeChoice = (index: number) => {
    setChoices((prev) => {
      const removed = prev[index];
      const next = prev.filter((_, i) => i !== index);
      setSelectedChoices((curr) => curr.filter((c) => c !== removed));
      if (next.length === 0) setAnswerMode("short");
      return next;
    });
  };

  const toggleSelectedChoice = (value: string) => {
    setSelectedChoices((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  // === 미리보기용 body ===

  const previewBody = useMemo<RestorationPostBody>(() => {
    const body: RestorationPostBody = { blocks };
    const trimmedChoices = choices.map((c) => c.trim()).filter(Boolean);
    if (trimmedChoices.length > 0) body.choices = trimmedChoices;
    if (answerMode === "short") {
      if (shortAnswer.trim()) body.correctAnswer = shortAnswer.trim();
    } else {
      const validSelected = selectedChoices.filter((c) =>
        trimmedChoices.includes(c)
      );
      if (validSelected.length === 1) body.correctAnswer = validSelected[0];
      else if (validSelected.length > 1) body.correctAnswer = validSelected;
    }
    return body;
  }, [blocks, choices, answerMode, shortAnswer, selectedChoices]);

  const previewProblemString = useMemo(
    () => blocksToProblemString(previewBody.blocks),
    [previewBody.blocks]
  );

  // === 제출 ===

  const resetForm = () => {
    setBlocks([{ type: "text", content: "" }]);
    setPreamble("");
    setClosing("");
    setChoices([]);
    setSelectedChoices([]);
    setShortAnswer("");
    setAnswerMode("short");
    setImageStates({});
    setProblemNumber(null);
  };

  const handleSubmit = () => {
    if (!isSignedIn) {
      toast.error("로그인 후 작성할 수 있습니다.");
      return;
    }
    if (sessionId === null) {
      toast.error("시험 회차를 선택해주세요.");
      return;
    }
    if (anyUploading) {
      toast.error("이미지 업로드가 완료되면 제출할 수 있습니다.");
      return;
    }
    const body: RestorationPostBody = { blocks: previewBody.blocks };
    if (previewBody.choices) body.choices = previewBody.choices;
    if (previewBody.correctAnswer !== undefined) {
      body.correctAnswer = previewBody.correctAnswer;
    }
    const err = validateBodyClient(body);
    if (err) {
      toast.error(err);
      return;
    }
    startTransition(async () => {
      if (isEditMode && editingPost) {
        const result = adminEditing
          ? await updatePostAsAdmin({
              postId: editingPost.id,
              body,
              preambleText: preamble.trim() || null,
              closingText: closing.trim() || null,
            })
          : await updateOwnPost({
              postId: editingPost.id,
              body,
              preambleText: preamble.trim() || null,
              closingText: closing.trim() || null,
            });
        if (result.success) {
          toast.success("게시물이 수정되었습니다.");
          onSuccess?.(null);
        } else {
          toast.error(result.error || "게시물 수정에 실패했습니다.");
        }
        return;
      }

      const result = await createPost({
        sessionId,
        body,
        preambleText: preamble.trim() || null,
        closingText: closing.trim() || null,
        problemNumber: isAdmin ? problemNumber : null,
        adminStatus: isAdmin ? adminStatus : undefined,
      });
      if (result.success) {
        toast.success("복원 게시물이 등록되었습니다.");
        onSuccess?.(result.post);
        resetForm();
      } else {
        toast.error(result.error || "게시물 등록에 실패했습니다.");
      }
    });
  };

  const currentSession = sessions.find((s) => s.id === sessionId) ?? null;
  const sessionDisabled =
    currentSession?.is_write_locked === true && !isAdmin;

  // === 렌더 ===

  if (sessions.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-gray-300 dark:border-neutral-600 p-6 text-center text-sm text-gray-500 dark:text-neutral-400">
        아직 등록된 시험 회차가 없습니다. 관리자가 회차를 먼저 생성해야 합니다.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {isAdmin && !isEditMode && (
        <div className="rounded-md border border-blue-200 dark:border-blue-900/60 bg-blue-50/60 dark:bg-blue-950/20 px-3 py-2 flex flex-col gap-1 text-xs text-blue-800 dark:text-blue-300">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>
              관리자 게시물 배지를 선택하세요. 작성 후에도 변경할 수 있습니다.
            </span>
          </div>
          <div className="flex items-center gap-3 pl-6">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="admin-status"
                value="confirmed"
                checked={adminStatus === "confirmed"}
                onChange={() => setAdminStatus("confirmed")}
              />
              <span>확정</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="admin-status"
                value="editing"
                checked={adminStatus === "editing"}
                onChange={() => setAdminStatus("editing")}
              />
              <span>종합중</span>
            </label>
          </div>
        </div>
      )}

      <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
      {/* === 편집 패널 === */}
      <div className="flex flex-col gap-5">
        <section>
          <label className="block text-sm font-medium mb-1">시험 회차</label>
          <select
            className="w-full rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm disabled:opacity-60"
            value={sessionId ?? ""}
            onChange={(e) => setUserSessionId(Number(e.target.value))}
            disabled={isEditMode}
          >
            {sessions.map((s) => {
              const locked = s.is_write_locked && !isAdmin;
              return (
                <option key={s.id} value={s.id} disabled={locked}>
                  {s.label}
                  {s.is_write_locked ? " (잠김)" : ""}
                </option>
              );
            })}
          </select>
          {isEditMode && (
            <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
              수정 중에는 회차를 변경할 수 없습니다.
            </p>
          )}
          {!isEditMode && currentSession?.is_write_locked && (
            <p className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <Lock className="w-3 h-3" />
              {isAdmin
                ? "잠긴 회차입니다. 관리자 권한으로 작성할 수 있습니다."
                : "이 회차는 글쓰기가 잠겼습니다."}
            </p>
          )}
        </section>

        {isAdmin && !isEditMode && (
          <section>
            <label className="block text-sm font-medium mb-1">
              문제 번호 <span className="text-xs text-gray-500">(관리자)</span>
            </label>
            <select
              className="w-full rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm"
              value={problemNumber ?? ""}
              onChange={(e) =>
                setProblemNumber(
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
            >
              <option value="">미분류로 등록</option>
              {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}번
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
              작성 시점에 문제 번호를 지정하면 해당 섹션 아코디언에 바로 표시됩니다.
            </p>
          </section>
        )}

        <section>
          <label className="block text-sm font-medium mb-1">
            문제 위 자유 텍스트 (선택)
          </label>
          <Textarea
            value={preamble}
            onChange={(e) => setPreamble(e.target.value)}
            placeholder="예: &quot;이 문제는 2번 문제 같은데 정확히 기억이 안 나요.&quot;"
            className="min-h-[60px]"
          />
        </section>

        <section>
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <label className="block text-sm font-medium">본문 블록</label>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => addBlock("text")}
              >
                <Plus className="w-3 h-3 mr-1" />
                텍스트
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => addBlock("code")}
              >
                <Plus className="w-3 h-3 mr-1" />
                코드
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => addBlock("table")}
              >
                <Plus className="w-3 h-3 mr-1" />표
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => addBlock("image")}
              >
                <Plus className="w-3 h-3 mr-1" />
                이미지
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {blocks.map((block, index) => (
              <BlockCard
                key={index}
                index={index}
                total={blocks.length}
                block={block}
                onChange={(next) => updateBlock(index, next)}
                onRemove={() => removeBlock(index)}
                onMoveUp={() => moveBlock(index, -1)}
                onMoveDown={() => moveBlock(index, 1)}
                onImageFile={(file) => handleImageFile(index, file)}
                imageState={imageStates[index] ?? "idle"}
              />
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">
              보기 (비우면 단답형)
            </label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addChoice}
            >
              <Plus className="w-3 h-3 mr-1" />
              보기 추가
            </Button>
          </div>
          {choices.length > 0 ? (
            <div className="flex flex-col gap-2">
              {choices.map((choice, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <span className="text-sm text-gray-500 w-6">
                    {index + 1}.
                  </span>
                  <Input
                    value={choice}
                    onChange={(e) => updateChoice(index, e.target.value)}
                    placeholder={`보기 ${index + 1}`}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeChoice(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500">단답형 문제로 등록됩니다.</p>
          )}
        </section>

        <section>
          <label className="block text-sm font-medium mb-1">정답</label>
          {answerMode === "short" ? (
            <Input
              value={shortAnswer}
              onChange={(e) => setShortAnswer(e.target.value)}
              placeholder="정답을 입력하세요"
            />
          ) : (
            <div className="flex flex-col gap-1">
              <p className="text-xs text-gray-500 mb-1">
                정답인 보기를 모두 선택하세요 (복수 선택 가능)
              </p>
              {choices
                .map((c) => c.trim())
                .filter(Boolean)
                .map((choice) => (
                  <label
                    key={choice}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedChoices.includes(choice)}
                      onChange={() => toggleSelectedChoice(choice)}
                    />
                    <span>{choice}</span>
                  </label>
                ))}
            </div>
          )}
        </section>

        <section>
          <label className="block text-sm font-medium mb-1">
            문제 아래 자유 텍스트 (선택)
          </label>
          <Textarea
            value={closing}
            onChange={(e) => setClosing(e.target.value)}
            placeholder="예: &quot;정답은 확실하지 않아요.&quot;"
            className="min-h-[60px]"
          />
        </section>

        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              취소
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              isPending || anyUploading || (!isEditMode && sessionDisabled)
            }
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                {isEditMode ? "수정 중..." : "등록 중..."}
              </>
            ) : isEditMode ? (
              "수정 완료"
            ) : (
              "게시물 등록"
            )}
          </Button>
        </div>
      </div>

      {/* === 미리보기 패널 === */}
      <div className="md:sticky md:top-[calc(var(--mobile-navbar-offset,4rem)+1rem)] md:self-start lg:top-4 transition-[top] duration-200 ease-out motion-reduce:transition-none">
        <div className="rounded-md border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900/40 p-4">
          <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider">
            실시간 미리보기
          </div>
          {preamble && (
            <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-neutral-300 mb-4">
              {preamble}
            </p>
          )}
          {previewProblemString ? (
            <ProblemAnswer
              id={previewId}
              problem={previewProblemString}
              correctAnswer={previewBody.correctAnswer ?? ""}
              choices={previewBody.choices}
              disableHistory
              alwaysShowChoices
              defaultSolutionOpen
            />
          ) : (
            <p className="text-sm text-gray-400 italic">
              블록을 추가하면 미리보기가 표시됩니다.
            </p>
          )}
          {closing && (
            <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-neutral-300 mt-4">
              {closing}
            </p>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

// === 블록 카드 ===

interface BlockCardProps {
  index: number;
  total: number;
  block: RestorationBlock;
  onChange: (next: RestorationBlock) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onImageFile: (file: File) => void;
  imageState: ImageUploadState;
}

function BlockCard({
  index,
  total,
  block,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  onImageFile,
  imageState,
}: BlockCardProps) {
  const isFirst = index === 0;
  const isLast = index === total - 1;

  return (
    <div className="rounded-md border border-gray-200 dark:border-neutral-700 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-gray-500">
          {block.type === "text"
            ? "텍스트"
            : block.type === "code"
              ? "코드"
              : block.type === "table"
                ? "표"
                : "이미지"}
        </span>
        <div className="flex gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            disabled={isFirst}
            onClick={onMoveUp}
          >
            <ArrowUp className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            disabled={isLast}
            onClick={onMoveDown}
          >
            <ArrowDown className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={onRemove}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>

      {block.type === "text" && (
        <Textarea
          value={block.content}
          onChange={(e) => onChange({ type: "text", content: e.target.value })}
          placeholder="텍스트를 입력하세요"
          className="min-h-[70px]"
        />
      )}

      {block.type === "code" && (
        <div className="flex flex-col gap-2">
          <select
            aria-label="코드 언어"
            className="w-32 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100"
            value={block.language ?? "txt"}
            onChange={(e) =>
              onChange({
                type: "code",
                content: block.content,
                language: e.target.value,
              })
            }
          >
            {block.language &&
              !BLOCK_LANGUAGES.includes(block.language) && (
                <option value={block.language} disabled>
                  {block.language}
                </option>
              )}
            {BLOCK_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
          <Textarea
            value={block.content}
            onChange={(e) =>
              onChange({
                type: "code",
                content: e.target.value,
                language: block.language,
              })
            }
            placeholder="코드를 붙여넣으세요"
            className="min-h-[120px] font-mono text-xs"
          />
        </div>
      )}

      {block.type === "table" && (
        <TableBlockEditor block={block} onChange={onChange} />
      )}

      {block.type === "image" && (
        <ImageBlockEditor
          block={block}
          imageState={imageState}
          onFile={onImageFile}
          onAltChange={(alt) =>
            onChange({ ...block, alt })
          }
        />
      )}
    </div>
  );
}

// === 표 블록 에디터 ===

interface TableBlockEditorProps {
  block: Extract<RestorationBlock, { type: "table" }>;
  onChange: (next: RestorationBlock) => void;
}

function TableBlockEditor({ block, onChange }: TableBlockEditorProps) {
  const { headers, rows } = block;

  const updateHeader = (index: number, value: string) => {
    const next = [...headers];
    next[index] = value;
    onChange({ type: "table", headers: next, rows });
  };

  const updateCell = (rowIdx: number, colIdx: number, value: string) => {
    const next = rows.map((r) => [...r]);
    next[rowIdx][colIdx] = value;
    onChange({ type: "table", headers, rows: next });
  };

  const addColumn = () => {
    onChange({
      type: "table",
      headers: [...headers, `열${headers.length + 1}`],
      rows: rows.map((r) => [...r, ""]),
    });
  };

  const removeColumn = (colIdx: number) => {
    if (headers.length <= 1) return;
    onChange({
      type: "table",
      headers: headers.filter((_, i) => i !== colIdx),
      rows: rows.map((r) => r.filter((_, i) => i !== colIdx)),
    });
  };

  const addRow = () => {
    onChange({
      type: "table",
      headers,
      rows: [...rows, headers.map(() => "")],
    });
  };

  const removeRow = (rowIdx: number) => {
    onChange({
      type: "table",
      headers,
      rows: rows.filter((_, i) => i !== rowIdx),
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <table className="text-xs border-collapse">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="p-1">
                  <div className="flex items-center gap-1">
                    <Input
                      value={h}
                      onChange={(e) => updateHeader(i, e.target.value)}
                      className="h-7 text-xs min-w-[80px]"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => removeColumn(i)}
                      disabled={headers.length <= 1}
                      className="h-6 w-6"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rIdx) => (
              <tr key={rIdx}>
                {row.map((cell, cIdx) => (
                  <td key={cIdx} className="p-1">
                    <Input
                      value={cell}
                      onChange={(e) => updateCell(rIdx, cIdx, e.target.value)}
                      className="h-7 text-xs min-w-[80px]"
                    />
                  </td>
                ))}
                <td className="p-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeRow(rIdx)}
                    className="h-6 w-6"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addColumn}
        >
          <Plus className="w-3 h-3 mr-1" />열 추가
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={addRow}>
          <Plus className="w-3 h-3 mr-1" />행 추가
        </Button>
      </div>
    </div>
  );
}

// === 이미지 블록 에디터 ===

interface ImageBlockEditorProps {
  block: Extract<RestorationBlock, { type: "image" }>;
  imageState: ImageUploadState;
  onFile: (file: File) => void;
  onAltChange: (alt: string) => void;
}

function ImageBlockEditor({
  block,
  imageState,
  onFile,
  onAltChange,
}: ImageBlockEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploading = imageState === "uploading";

  return (
    <div className="flex flex-col gap-2">
      {block.url ? (
        <div className="relative w-32 h-32 rounded-md overflow-hidden border border-gray-200 dark:border-neutral-600">
          <Image
            src={block.url}
            alt={block.alt || "첨부 이미지"}
            fill
            sizes="128px"
            className="object-cover"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 dark:border-neutral-600 p-4 text-sm text-gray-500 hover:border-blue-400 disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              업로드 중...
            </>
          ) : (
            <>
              <ImagePlus className="w-4 h-4" />
              이미지 선택
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
      <Input
        value={block.alt ?? ""}
        onChange={(e) => onAltChange(e.target.value)}
        placeholder="대체 텍스트(선택) - 이미지 설명"
        className="text-xs"
      />
      {block.url && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline self-start"
        >
          다른 이미지로 교체
        </button>
      )}
    </div>
  );
}
